import { Injectable }       from '@angular/core';
import { NgbModal }         from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';

import { ApiService }                              from '../api/api.service';
import { CarfaxService }                           from '../carfax/carfax.service';
import { CheckaService }                           from '../../app/checka/checka.service';
import { BigDecimalService }                       from '../core/services/big-decimal.service';
import { CompanyIntegrationsModel }                from '../companies/models/company.integrations';
import { CompanyService }                          from '../companies/company.service';
import { ConfirmService }                          from '../messages/confirm/confirm.service';
import { DvlaLookupService }                       from '../dvla-lookup/dvla-lookup.service';
import { IntegrationQuotePricesModel }             from '../integrations/typings/integration.quote-prices';
import { MessagesService }                         from '../messages/messages.service';
import { PurchaseRequestLogModel }                 from '../../app/reports/models/purchase-request.log';
import { SessionService }                          from '../sessions/session.service';
import { TableQueryParameters }                    from '../table/types/table-query-parameters';
import { VehicleModel }                            from '../vehicles/models/vehicle';
import { VehicleRegistrationCheckaQuoteModel }     from './typings/vehicle-registration.checka-quote';
import { VehicleRegistrationPurchaseComponent }    from './vehicle-registration.purchase.component';
import { VehicleRegistrationRequestQuoteModel }    from './typings/vehicle-registration.request-quote';
import { VehicleRegistrationTireDetailsComponent } from './vehicle-registration.tire-details.component';
import { VehicleRegistrationTireModel }            from './typings/vehicle-registration.tire';

@Injectable({
  providedIn: 'root'
})
export class VehicleRegistrationService {
  public title = this.translateService.instant('COMPANY_SPACE.INTEGRATIONS.VEHICLE_REGISTRATIONS.LOOKUP');

  constructor (
    private apiService       : ApiService,
    private bigDecimalService: BigDecimalService,
    private carfaxService    : CarfaxService,
    private checkaService    : CheckaService,
    private companyService   : CompanyService,
    private confirmService   : ConfirmService,
    private dvlaLookupService: DvlaLookupService,
    private messagesService  : MessagesService,
    private modalService     : NgbModal,
    private sessionService   : SessionService,
    private translateService : TranslateService
  ) {}

  activate () : Promise<any> {
    return this.confirmService.generic(this.translateService.instant('JS_SPACE.INTEGRATIONS.ACTIVATE', {
      name: this.title
    }))
    .then(() => this.companyService.country('australia')
      ? this.apiService.get('/company_integration/add_registration_lookup')
      : this.apiService.get('/company/add_checka')
    )
    .then(() => this.sessionService.refresh());
  }

  confirmPurchase ( quote : VehicleRegistrationRequestQuoteModel ) : Promise<any> {
    const request = this.companyService.country('australia')
      ? this.apiService.get('/purchase_integration_lookups/purchase/true', [ quote.quantity, 'registration_lookup' ])
      : this.apiService.post('/chargify/purchase', {
          quantity: quote.quantity,
          service : 'checka'
        });

    return request.then(() => this.sessionService.refresh());
  }

  deactivate () : Promise<any> {
    return this.confirmService.generic(this.translateService.instant('JS_SPACE.INTEGRATIONS.DEACTIVATE', {
      name: this.title
    }))
    .then(() => this.companyService.country('australia')
      ? this.apiService.get('/company_integration/remove_registration_lookup')
      : this.apiService.get('/company/remove_checka')
    )
    .then(() => this.sessionService.refresh());
  }

  findVehicle ( vehicle : VehicleModel ) : Promise<VehicleModel> {
    return this.apiService.post('/vehicles/find_on_registration_lookup', vehicle, 'vehicle');
  }

  getCurrentPrice ( quantity : number, prices : IntegrationQuotePricesModel ) : string {
    if ( quantity < 300 ) {
      return prices.high_price;
    }

    if ( quantity < 500 ) {
      return prices.medium_price;
    }

    return quantity >= 500
      ? prices.low_price
      : '0.00';
  }

  getLookupCount () : Promise<number> {
    return this.apiService.get('/dashboard/purchased_request_logs_count');
  }

  getLookups ( params : TableQueryParameters = {} ) : Promise<Array<PurchaseRequestLogModel>> {
    return this.apiService.get('/dashboard/purchased_request_logs_index', [
        params.pageSize || 5,
        params.offset   || 0
      ]);
  }

  getQuote ( quote : VehicleRegistrationRequestQuoteModel ) : Promise<VehicleRegistrationRequestQuoteModel> {
    if ( !quote.quantity ) {
      quote.total = '0.00';

      return Promise.resolve(quote);
    }

    if ( this.companyService.country('australia') ) {
      return this.apiService.get('/purchase_integration_lookups/quote', [ quote.quantity, 'registration_lookup' ])
        .then(( total : string ) => {
          quote.total = total;

          return quote;
        });
    }

    quote.total = this.bigDecimalService.get(quote.price || '0.00')
      .multiply(this.bigDecimalService.get(quote.quantity))
      .toString();

    return Promise.resolve(quote);
  }

  getRequestPrices () : Promise<IntegrationQuotePricesModel> {
    return this.companyService.country('australia')
      ? this.apiService.get('/purchase_integration_lookups/current_lookup_cost/registration_lookup')
      : this.apiService.get('/chargify/service_cost', 'checka')
        .then(( result : VehicleRegistrationCheckaQuoteModel ) => ({
          high_price  : result.price.toString(),
          low_price   : result.price.toString(),
          medium_price: result.price.toString()
        }));
  }

  getRequests () : Promise<number> {
    return this.companyService.country('australia')
      ? this.apiService.get('/company_integrations')
        .then(( settings : CompanyIntegrationsModel ) => settings.registration_lookup_count)
      : Promise.resolve(this.companyService.company.checka_requests);
  }

  getTireDetails ( vehicle : VehicleModel ) : Promise<VehicleRegistrationTireModel> {
    return this.apiService.get('/vehicles/get_tyre_details', vehicle.registration_lookup_vehicle_id)
      .then(( details : VehicleRegistrationTireModel ) => {
        details.Front.Tyres.FitmentType = details.FitmentType;
        details.Front.Tyres.RunFlat     = details.RunFlat;
        details.Rear.Tyres.FitmentType  = details.FitmentType;
        details.Rear.Tyres.RunFlat      = details.RunFlat;

        return details;
      })
      .catch(( err : any ) => {
        if ( this.hasInsufficientRequests(err) ) {
          this.messagesService.error(this.translateService.instant('JS_SPACE.CHECKA.INSUFFICIENT_REQUESTS'));

          setTimeout(() => {
            this.purchaseRequests()
              .then(() => this.getTireDetails(vehicle));
          }, 0);
        }

        return Promise.reject(err);
      });
  }

  hasInsufficientRequests ( err : any ) : boolean {
    const errorData = err?.error || err?.data || err;

    return errorData?.type === 'Q';
  }

  hasRegistrationId ( vehicle : VehicleModel ) : boolean {
    return !!vehicle?.registration_lookup_vehicle_id;
  }

  purchaseRequests () : Promise<any> {
    return this.getRequestPrices()
      .then(( prices : IntegrationQuotePricesModel ) => {
        const modal = this.modalService.open(VehicleRegistrationPurchaseComponent);

        modal.componentInstance.prices = prices;

        return modal.result.then(( result : any ) => {
          this.confirmService.notify({
            message: this.translateService.instant('JS_SPACE.MESSAGES.THANK_YOU_PURCHASE_MESSAGE')
          });

          return result;
        });
      });
  }

  search ( vehicle : VehicleModel ) : Promise<VehicleModel> {
    let request: Promise<VehicleModel>;

    if ( this.companyService.hasIntegration('vehicle-registrations') ) {
      request = this.companyService.country('newZealand')
        ? this.checkaService.search(vehicle)
        : this.findVehicle(vehicle);

      return request.catch(( err : any ) => {
        if ( this.hasInsufficientRequests(err) ) {
          this.messagesService.error(this.translateService.instant('JS_SPACE.CHECKA.INSUFFICIENT_REQUESTS'));

          setTimeout(() => {
            this.purchaseRequests()
              .then(() => this.search(vehicle));
          }, 0);
        }

        return Promise.reject(err);
      });
    }

    if ( this.companyService.hasIntegration('dvla-lookup') ) {
      return this.dvlaLookupService.search(vehicle);
    }

    if ( this.companyService.hasIntegration('carfax') ) {
      return this.carfaxService.search(vehicle);
    }

    return Promise.resolve({} as VehicleModel);
  }

  viewTireDetails ( vehicle : VehicleModel ) : Promise<any> {
    return this.getTireDetails(vehicle)
      .then(( tire : VehicleRegistrationTireModel ) => {
        const modal = this.modalService.open(VehicleRegistrationTireDetailsComponent, {
          windowClass: 'x-lg-modal'
        });

        modal.componentInstance.tire = tire;

        return modal.result;
      });
  }
}
